import { useState } from 'react';
import * as yup from 'yup';
import { Formik } from 'formik';
import { Box } from '@mui/material';
import { DropDown, ChipInput, ChipInputTagErrors } from '@qrios/frontend-library';

import { FORM_INPUT_ERROR_MSGS } from '../../../../../../constants/errors';
import DateRangePicker from '../../../../../common/fields/date-range-picker';
import { billingEndDateValidation, msisdnValidationSimple } from '../../../../../../constants/validation';
import { FILTERS, BILLING_STATUSES, PAGE_SIZES, OPERATORS } from '../../../../../common/filters/constants';
import { WithFilterFormLayout, FilterFormProps } from '../../../../../common/filters';

import locales from '../locales';
import { AirtimeBillingFilters } from './interfaces';
import { activeFiltersCountMapper, initFilters } from './filters';

const validationSchema = yup.object().shape({
  from: yup.date().required().typeError(FORM_INPUT_ERROR_MSGS.INVALID_FORMAT('from date')),
  to: billingEndDateValidation,
  acquirer_msisdn: yup.array(msisdnValidationSimple)
});

const FilterForm = ({ onSubmit, onClose, activeFilters }: FilterFormProps<AirtimeBillingFilters>) => {
  const initialFiltersDifferentThanActiveFilters = activeFiltersCountMapper(activeFilters) !== 0;
  const [filtersChanged, setFilterChanged] = useState<boolean>(initialFiltersDifferentThanActiveFilters);
  const [initialValues, setInitialValues] = useState<AirtimeBillingFilters>({
    from: activeFilters.from,
    to: activeFilters.to,
    operation_id: activeFilters.operation_id,
    status: activeFilters.status,
    page_size: activeFilters.page_size,
    sku: activeFilters.sku,
    operator: activeFilters.operator,
    acquirer_msisdn: activeFilters.acquirer_msisdn
  });

  const closeOrClearFilters = () => {
    if (!filtersChanged) {
      onClose();
    }
    setInitialValues(initFilters);
    setFilterChanged(false);
  };
  return (
    <Box>
      <Formik
        enableReinitialize
        validationSchema={validationSchema}
        initialValues={initialValues}
        onSubmit={(filters: AirtimeBillingFilters) => {
          onSubmit({
            ...filters,
            acquirer_msisdn: filters.acquirer_msisdn
          });
        }}
      >
        {({ values, handleSubmit, setFieldValue, errors, touched, handleBlur }) => {
          const handleChange = (field: string, value: unknown) => {
            setFieldValue(field, value);
            setFilterChanged(true);
          };

          return (
            <WithFilterFormLayout
              onSecondary={closeOrClearFilters}
              onSubmit={handleSubmit}
              filtersChanged={filtersChanged}
            >
              <DateRangePicker
                from={{ name: FILTERS.FROM, value: values.from, error: errors.from, touched: touched.from }}
                to={{ name: FILTERS.TO, value: values.to, error: errors.to, touched: touched.to }}
                handleFromChange={value => handleChange(FILTERS.FROM, value)}
                handleToChange={value => handleChange(FILTERS.TO, value)}
                handleBlur={handleBlur}
                variant="nowEnabled"
              />
              <DropDown
                label={locales.common.filters.operators.label}
                name={FILTERS.OPERATOR}
                value={values.operator as string[]}
                options={OPERATORS}
                handleChange={handleChange}
                handleBlur={handleBlur}
                multiple={true}
                placeholder={locales.common.filters.operators.placeholder}
              />
              <ChipInput
                error={errors.sku?.[(errors.sku ?? []).length - 1]}
                label={locales.common.filters.skus.label}
                value={values.sku as string[]}
                handleChange={handleChange}
                placeholder={locales.common.filters.skus.placeholder}
                handleBlur={handleBlur}
                name={FILTERS.SKU}
              />
              <DropDown
                label={locales.common.filters.statuses.label}
                value={values.status}
                options={BILLING_STATUSES}
                name={FILTERS.STATUS}
                handleChange={handleChange}
                handleBlur={handleBlur}
                multiple={true}
                placeholder={locales.common.filters.statuses.placeholder}
              />
              <ChipInput
                error={errors.operation_id?.[(errors.operation_id ?? []).length - 1]}
                label={locales.common.filters.operationIds.label}
                value={values.operation_id}
                name={FILTERS.OPERATION_ID}
                handleChange={handleChange}
                handleBlur={handleBlur}
                placeholder={locales.common.filters.operationIds.placeholder}
                tagErrors={errors.operation_id as ChipInputTagErrors}
              />
              <ChipInput
                error={errors.acquirer_msisdn?.[(errors.acquirer_msisdn ?? []).length - 1]}
                label={locales.common.filters.acquirer_msisdn.label}
                value={values.acquirer_msisdn}
                name={FILTERS.ACQUIRER_MSISDN}
                handleChange={handleChange}
                handleBlur={handleBlur}
                placeholder={locales.common.filters.acquirer_msisdn.placeholder}
                tagErrors={errors.acquirer_msisdn as ChipInputTagErrors}
              />
              <DropDown
                label={locales.common.filters.resultsNumber.label}
                value={values.page_size}
                options={PAGE_SIZES}
                name={FILTERS.PAGE_SIZE}
                handleChange={handleChange}
                handleBlur={handleBlur}
                disableClearable={true}
              />
            </WithFilterFormLayout>
          );
        }}
      </Formik>
    </Box>
  );
};

export default FilterForm;
